/**
 * LangSmith URL helpers for Horizon chat / Observer surfaces.
 *
 * Thread links use the same smith.langchain.com project+thread shape as AI Thread Observer.
 * Project id comes from `REACT_APP_LANGSMITH_PROJECT_ID` (per-env client.ini); workspace and
 * fallback project match the Observer defaults so local/dev builds still resolve.
 */

/** Default LangSmith org/workspace UUID (same as Observer). */
export const DEFAULT_LANGSMITH_WORKSPACE_ID = "44975c4f-7463-4174-a1b2-7390e12b2622";

/** Fallback project UUID when `REACT_APP_LANGSMITH_PROJECT_ID` is unset (Observer default / typically dev). */
export const DEFAULT_LANGSMITH_PROJECT_ID = "20f0ccc5-d50a-46f8-8f72-3e8470aa7a3c";

/**
 * Resolve LangSmith project UUID from CRA env, with Observer-compatible fallback.
 */
export function getLangSmithProjectId(): string {
  return String(process.env.REACT_APP_LANGSMITH_PROJECT_ID || "").trim() || DEFAULT_LANGSMITH_PROJECT_ID;
}

/**
 * Resolve LangSmith workspace/org UUID from CRA env, with Observer-compatible fallback.
 */
export function getLangSmithWorkspaceId(): string {
  return String(process.env.REACT_APP_LANGSMITH_WORKSPACE_ID || "").trim() || DEFAULT_LANGSMITH_WORKSPACE_ID;
}

/**
 * Build the LangSmith UI URL for a Theo AI thread (`/t/{aiThreadId}` filter on the project).
 *
 * @param aiThreadId - Cortex `ai_threads.ai_thread_id` (string or number)
 * @returns Absolute smith.langchain.com URL, or empty string when id is missing/invalid
 */
export function buildLangSmithThreadUrl(aiThreadId: string | number | null | undefined): string {
  const threadId = String(aiThreadId ?? "").trim();
  if (!threadId || threadId === "null" || threadId === "undefined") {
    return "";
  }
  const workspaceId = getLangSmithWorkspaceId();
  const projectId = getLangSmithProjectId();
  return `https://smith.langchain.com/o/${workspaceId}/projects/p/${projectId}/t/${encodeURIComponent(threadId)}`;
}
